import { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useNotificationStore } from '../../store/storeNotifications';

import { Position, useEdges, useReactFlow } from 'reactflow';

import '../../assets/styles/animatedBorder.scss';
import { NodeProps } from '../../util/Types/NodeProps';
import useFlowsStore from '../../store/storeFlows';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { Box, TextField, Typography } from '@mui/material';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import Node from '../UI/Node/Node';

import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { useAuthStore } from '../../store/storeAuth';
import { FlowService } from '../../service/FlowService';
import InputHandle from '../UI/InputHandle/InputHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { arrayDataToText } from '../../util/dataToText';

const FlowCreator = ({ id, data, type }: NodeProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setNodes: setNodeState, setEdges: setEdges } = useReactFlow();
  const reactflow = useReactFlow();
  const { flowId, setSaving, setSchema } = useFlowsStore(state => state);
  const { setNotification } = useNotificationStore(state => state);
  const edges = useEdges();

  const [btnLoading, setBtnLoading] = useState(false);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState({
    value: '',
    name: '',
  });

  // Track previous connection state to prevent loops
  const prevConnectionsRef = useRef({
    nodesConnected: false,
    edgesConnected: false,
    nodesCleared: false,
    edgesCleared: false
  });

  // Track the source nodes for incoming connections
  const connectedSourcesRef = useRef({
    nodesInput: '',
    edgesInput: ''
  });

  const [formData, setFormData] = useState({
    nodes: '',
    edges: '',
  });

  // Initialize data from connections when component loads
  useEffect(() => {
    // Find all incoming edges to this node
    const incomingNodesEdge = edges.find(
      edge => edge.target === id && edge.targetHandle === 'nodesInput'
    );
    
    const incomingEdgesEdge = edges.find(
      edge => edge.target === id && edge.targetHandle === 'edgesInput'
    );

    // Handle nodes connection/disconnection
    const hasNodesConnection = !!incomingNodesEdge;
    const hadNodesConnection = prevConnectionsRef.current.nodesConnected;
    const nodesDataExists = !!(data?.nodesData?.length > 0 || data?.nodes);
    
    // Only process nodes when connection state changes
    if (hasNodesConnection !== hadNodesConnection) {
      prevConnectionsRef.current.nodesConnected = hasNodesConnection;
      
      if (hasNodesConnection) {
        // Connection was just added
        prevConnectionsRef.current.nodesCleared = false;
        connectedSourcesRef.current.nodesInput = incomingNodesEdge!.source;
        const sourceNode = reactflow.getNodes().find(node => node.id === incomingNodesEdge!.source);
        if (sourceNode?.data?.text && (!data?.nodesData || data.nodesData.length === 0 || !data?.nodes)) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: {
              nodes: sourceNode.data.text
            },
            setSchema,
          });
        }
      } else if (nodesDataExists && !prevConnectionsRef.current.nodesCleared) {
        // Connection was just removed and we haven't cleared the data yet
        prevConnectionsRef.current.nodesCleared = true;
        connectedSourcesRef.current.nodesInput = '';
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            nodes: '',
            nodesData: [] 
          },
          setSchema,
        });
      }
    }

    // Handle edges connection/disconnection
    const hasEdgesConnection = !!incomingEdgesEdge;
    const hadEdgesConnection = prevConnectionsRef.current.edgesConnected;
    const edgesDataExists = !!(data?.edgesData?.length > 0 || data?.edges);
    
    // Only process edges when connection state changes
    if (hasEdgesConnection !== hadEdgesConnection) {
      prevConnectionsRef.current.edgesConnected = hasEdgesConnection;
      
      if (hasEdgesConnection) {
        // Connection was just added
        prevConnectionsRef.current.edgesCleared = false;
        connectedSourcesRef.current.edgesInput = incomingEdgesEdge!.source;
        const sourceNode = reactflow.getNodes().find(node => node.id === incomingEdgesEdge!.source);
        if (sourceNode?.data?.text && (!data?.edgesData || data.edgesData.length === 0 || !data?.edges)) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: {
              edges: sourceNode.data.text
            },
            setSchema,
          });
        }
      } else if (edgesDataExists && !prevConnectionsRef.current.edgesCleared) {
        // Connection was just removed and we haven't cleared the data yet
        prevConnectionsRef.current.edgesCleared = true;
        connectedSourcesRef.current.edgesInput = '';
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            edges: '',
            edgesData: [] 
          },
          setSchema,
        });
      }
    }
  }, [edges, id, data, reactflow, setNodeState, setSaving, flowId, setSchema]);

  // Watch for changes to source nodes' data and update this node accordingly
  useEffect(() => {
    const nodes = reactflow.getNodes();
    const nodesSource = connectedSourcesRef.current.nodesInput;
    const edgesSource = connectedSourcesRef.current.edgesInput;
    
    if (nodesSource) {
      const sourceNode = nodes.find(node => node.id === nodesSource);
      if (sourceNode?.data?.text) {
        const currentNodesText = data?.nodesData?.length > 0 
          ? arrayDataToText(data.nodesData) 
          : data?.nodes || '';
          
        if (sourceNode.data.text !== currentNodesText) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: {
              nodes: sourceNode.data.text
            },
            setSchema,
          });
        }
      }
    }
    
    if (edgesSource) {
      const sourceNode = nodes.find(node => node.id === edgesSource);
      if (sourceNode?.data?.text) {
        const currentEdgesText = data?.edgesData?.length > 0 
          ? arrayDataToText(data.edgesData) 
          : data?.edges || '';
          
        if (sourceNode.data.text !== currentEdgesText) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: {
              edges: sourceNode.data.text
            },
            setSchema,
          });
        }
      }
    }
  }, [reactflow.getNodes, edges, id, data, setSaving, setNodeState, flowId, setSchema]);

  useDebounceEffect(
    () => {
      if (!isChangingDirectly) {
        setFormData({
          nodes: data?.nodesData?.length > 0 ? arrayDataToText(data?.nodesData) : data?.nodes || '',
          edges: data?.edgesData?.length > 0 ? arrayDataToText(data?.edgesData) : data?.edges || '',
        });
      }
    },
    [data, isChangingDirectly],
    300,
  );

  const onClick = async () => {
    setBtnLoading(true);
    let nodeJson: any[] = [];
    let edgeJson: any[] = [];

    try {
      if (data?.nodesData?.length > 0) {
        const nodeText = arrayDataToText(data.nodesData);
        try {
          nodeJson = JSON.parse(nodeText);
        } catch (error) {
          setNotification({
            message: 'Invalid JSON in connected Nodes data',
            type: 'error',
          });
          setBtnLoading(false);
          return;
        }
      } else if (formData.nodes) {
        try {
          nodeJson = JSON.parse(formData.nodes);
        } catch (error) {
          setNotification({
            message: 'Invalid JSON Request Body',
            type: 'error',
          });
          setBtnLoading(false);
          return;
        }
      }

      if (data?.edgesData?.length > 0) {
        const edgeText = arrayDataToText(data.edgesData);
        try {
          edgeJson = JSON.parse(edgeText);
        } catch (error) {
          setNotification({
            message: 'Invalid JSON in connected Edges data',
            type: 'error',
          });
          setBtnLoading(false);
          return;
        }
      } else if (formData.edges) {
        try {
          edgeJson = JSON.parse(formData.edges);
        } catch (error) {
          setNotification({
            message: 'Invalid JSON Auth',
            type: 'error',
          });
          setBtnLoading(false);
          return;
        }
      }

      const nodes = reactflow.getNodes();
      const filteredNodes = nodes.filter(
        node =>
          !nodeJson.some((n: any) => n.id === node.id) &&
          node?.type !== 'globalStarter',
      );
      const filteredEdges = edges.filter(
        edge =>
          !edgeJson.some((e: any) => e.id === edge.id) &&
          !edge?.source?.includes('globalStarter'),
      );

      await FlowService.nodesAutomation(flowId!, {
        nodes: [...filteredNodes, ...nodeJson],
        edges: [...filteredEdges, ...edgeJson],
      });

      setNodeState([...filteredNodes, ...nodeJson]);
      setEdges([...filteredEdges, ...edgeJson]);

    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Something went wrong',
      });
    } finally {
      setBtnLoading(false);
    }
  };

  const onChange = useCallback(
    (event: { target: { name: string; value: any } }) => {
      setWhoIsChanging({
        value: event.target.value,
        name: event.target.name,
      });

      setFormData({
        ...formData,
        [event.target.name]: event.target.value,
      });

      setIsChangingDirectly(true);
    },
    [id],
  );

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  const isNodesReadOnly = data?.nodesData && data.nodesData.length > 0;
  const isEdgesReadOnly = data?.edgesData && data.edgesData.length > 0;

  return (
    <>
      <Box className={isLoading || btnLoading ? 'node' : ''}>
        <Node
          edges={edges}
          type={type}
          isGlobal={false}
          onSubmit={onClick}
          btnText="Automate Nodes"
          isLoading={isLoading || btnLoading}
          data={data}
          id={id}
          showTokensUsed={false}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              p: '24px',
              width: '1000px',
              position: 'relative',
            }}
          >
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', marginBottom: '8px' }}>
                <Typography variant="h3" sx={{ position: 'relative' }}>
                  Nodes JSON
                  <InputHandle
                    handleId="nodesInput"
                    isActive={isActiveEdge(edges, id, "nodesInput", "target")}
                    activeColor={findColor?.color}
                    type="target"
                    position={Position.Left}
                    left={-34}
                    top="50%"
                  />
                </Typography>
              </Box>
              <TextField
                name="nodes"
                placeholder="Enter nodes JSON"
                onChange={onChange}
                value={isNodesReadOnly ? arrayDataToText(data.nodesData) : formData.nodes}
                multiline
                rows={10}
                fullWidth
                InputProps={{
                  readOnly: isNodesReadOnly,
                }}
                sx={{
                  bgcolor: isNodesReadOnly ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                }}
              />
            </Box>
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', marginBottom: '8px' }}>
                <Typography variant="h3" sx={{ position: 'relative' }}>
                  Edges JSON
                  <InputHandle
                    handleId="edgesInput"
                    isActive={isActiveEdge(edges, id, "edgesInput", "target")}
                    activeColor={findColor?.color}
                    type="target"
                    position={Position.Left}
                    left={-34}
                    top="50%"
                  />
                </Typography>
              </Box>
              <TextField
                name="edges"
                placeholder="Enter edges JSON"
                onChange={onChange}
                value={isEdgesReadOnly ? arrayDataToText(data.edgesData) : formData.edges}
                multiline
                rows={10}
                fullWidth
                InputProps={{
                  readOnly: isEdgesReadOnly,
                }}
                sx={{
                  bgcolor: isEdgesReadOnly ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                }}
              />
            </Box>
          </Box>
        </Node>
      </Box>
    </>
  );
};

export default memo(FlowCreator);
